/**
 * @module cercaliagl/layer/RainWeather
 */

import EventEmitter from 'eventemitter3';

/**
 * @enum {string}
 */
export const EventType = {
  FRAME_CHANGED: 'frameChanged',
};

/**
 * @const
 * @type {string}
 */
const LayerId = 'rain-weather-layer';

/**
 * @const
 * @type {string}
 */
const LayerUrl = 'https://api.rainviewer.com/public/weather-maps.json';

/**
 * @const
 * @type {string}
 */
const SourceId = 'rain-weather-source';

/**
 * @classdesc
 */
class RainWeather extends EventEmitter {
  /**
   * @constructor
   */
  constructor() {
    super();
    /**
     * @private
     * @type {string}
     */
    this.id_ = LayerId;

    /**
     * @private
     * @type {import("maplibre-gl").Map}
     */
    this.map_ = null;

    /**
     * @private
     * @type {boolean}
     */
    this.visible_ = true;

    /**
     * @private
     * @type {string}
     */
    this.beforeLayerId_ = 'poi-level-4';

    /**
     * @private
     * @type {typeof setInterval}
     */
    this.framesInterval = undefined;

    /**
     * @private
     * @type {typeof setInterval}
     */
    this.opacityInterval = undefined;

    /**
     * @private
     * @type {Array<string>}
     */
    this.currentFramesIdShown = [];
  }

  /**
   * @return {string} Id.
   */
  getLayerId() {
    return this.id_;
  }

  /**
   * @param {import("../Map").default} map MAp
   */
  setMap(map) {
    if (map) {
      this.map_ = map.getMapboxglMap();
      this.createLayer();
    } else {
      this.removeLayerData();
    }
  }

  /**
   * Creates a rain layer by fetching data from the specified LayerUrl and adding it to the map.
   * @private
   */
  createLayer() {
    fetch(LayerUrl)
      .then((res) => res.json())
      .then((apiData) => {
        const pastAndCurrentCast = [...apiData.radar.past, ...apiData.radar.nowcast];
        this.addRainLayer(apiData, pastAndCurrentCast);
        this.manageFrames(pastAndCurrentCast);
      });
  }

  /**
   * Adds a rain layer to the map.
   * @param {Object} apiData - The API data object.
   * @param {Array} pastAndCurrentCast - An array of past and current weather forecast frames.
   * @private
   */
  addRainLayer(apiData, pastAndCurrentCast) {
    const currentBeforeLayerId = this.map_.getLayer(this.beforeLayerId_) ? this.beforeLayerId_ : null;
    pastAndCurrentCast.forEach((frame) => {
      const currentSourceId = `${SourceId}_${frame.path}`;
      const currentLayerId = `${this.id_}_${frame.path}`;
      this.currentFramesIdShown.push(frame.path);
      this.map_.addSource(currentSourceId, {
        type: 'raster',
        tiles: [
          `${apiData.host + frame.path}/256/{z}/{x}/{y}/2/1_1.png`,
        ],
        tileSize: 256,
      });
      this.map_.addLayer({
        id: currentLayerId,
        type: 'raster',
        source: currentSourceId,
        layout: { visibility: 'none' },
        minzoom: 0,
        maxzoom: 12,
      }, currentBeforeLayerId);
    });
  }

  /**
   * Manages the frames of the RainWeather layer.
   * @param {Array} pastAndCurrentCast - An array containing the past and current weather forecast frames.
   * @private
   */
  manageFrames(pastAndCurrentCast) {
    let i = 0;
    this.framesInterval = setInterval(() => {
      if (i > pastAndCurrentCast.length - 1) {
        i = 0;
      }

      // Reset opacity and visibility of all frames
      pastAndCurrentCast.forEach((frame) => {
        this.map_.setPaintProperty(`${this.id_}_${frame.path}`, 'raster-opacity', 1);
        this.map_.setLayoutProperty(`${this.id_}_${frame.path}`, 'visibility', 'none');
      });

      // Show current and previous frame
      this.map_.setLayoutProperty(`${this.id_}_${pastAndCurrentCast[i].path}`, 'visibility', 'visible');
      this.emit('frameChanged', pastAndCurrentCast[i].time);
      if (i - 1 >= 0) {
        this.map_.setLayoutProperty(`${this.id_}_${pastAndCurrentCast[i - 1].path}`, 'visibility', 'visible');
        const frame = pastAndCurrentCast[i - 1];
        let opacity = 1;
        if (this.opacityInterval) {
          clearInterval(this.opacityInterval);
        }
        this.opacityInterval = setInterval(() => {
          this.manageFrameOpacity(opacity, frame.path);
          opacity -= 0.1;
        }, 50);
      }
      i += 1;
    }, 1000);
  }

  /**
   * Manages the opacity of the frames.
   * @param {number} opacity - The opacity value.
   * @param {string} framePath - The frame path.
   * @private
  */
  manageFrameOpacity(opacity, framePath) {
    if (opacity <= 0) {
      clearInterval(this.opacityInterval);
      this.opacityInterval = null;
      this.map_.setLayoutProperty(`${this.id_}_${framePath}`, 'visibility', 'none'); // Hide previous frame when opacity turns 0
      return;
    }
    this.map_.setPaintProperty(`${this.id_}_${framePath}`, 'raster-opacity', opacity);
  }

  /**
   * Clear frames intervals
   * @private
  */
  clearIntervals() {
    if (this.framesInterval) {
      clearInterval(this.framesInterval);
    }
    if (this.opacityInterval) {
      clearInterval(this.opacityInterval);
    }
  }

  /**
   * Removes layer data from the map and clears intervals.
   * @private
   */
  removeLayerData() {
    this.clearIntervals();
    this.currentFramesIdShown.forEach((frameId) => {
      this.map_.removeLayer(`${this.id_}_${frameId}`);
      this.map_.removeSource(`${SourceId}_${frameId}`);
    });
    // this.map_.removeSource(SourceId);
    this.currentFramesIdShown = [];
  }

  /**
   * @return {Array.<Object.<string, any>>} Layers
   */
  getLayers() {
    return this.layers_;
  }
}

export default RainWeather;
